import { useHandleCardPositions } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/useHandleCardPositions'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useEffect, useRef } from 'react'
import { UseEditorWithSidebarReturn } from '../types/types'
import { useTrackChangeCards } from './useTrackChangeCards'

export const SIDEBAR_WIDTH_EXPANDED = '100%'
export const SIDEBAR_WIDTH_COLLAPSED = '100%'

/**
 * Hook to manage the editor with a sidebar for track changes.
 * It provides the visibility state of the sidebar and a function to toggle it.
 * It also handles the logic for updating card positions when the sidebar is toggled.
 * @returns {UseEditorWithSidebarReturn} An object containing the sidebar visibility state and a toggle function.
 * @property {boolean} isSidebarVisible - Indicates if the sidebar is currently visible.
 */
export const useEditorWithSidebar = (): UseEditorWithSidebarReturn => {
    const {
        contentItemViewNodes,
        isTrackChangesExpanded,
        setIsTrackChangesExpanded,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        isTrackChangesExpanded: state.isTrackChangesExpanded,
        setIsTrackChangesExpanded: state.actions.setIsTrackChangesExpanded,
    }))

    // capture previous sidebar visibility state in ref to check if it has changed in ref
    const sidebarVisibilityRef = useRef<boolean>(false)
    const handleCardPositions = useHandleCardPositions()
    const { isNotDefaultSuggestionCardFilter } = useTrackChangeCards()

    useEffect(() => {
        sidebarVisibilityRef.current = isTrackChangesExpanded as boolean
    }, [isTrackChangesExpanded])

    const toggleSidebar = useCallback(
        (expandSidebar?: boolean) => {
            if (!isNotDefaultSuggestionCardFilter && expandSidebar) {
                handleCardPositions()
            }

            if (setIsTrackChangesExpanded) {
                setIsTrackChangesExpanded(expandSidebar as boolean)
            }
        },
        [
            contentItemViewNodes,
            isNotDefaultSuggestionCardFilter,
            handleCardPositions,
        ]
    )

    return {
        isSidebarVisible: isTrackChangesExpanded as boolean,
        toggleSidebar,
        sidebarVisibilityRef,
    }
}
